/**
 * useCustomNav — manages custom navigation items stored in ContentEditing.
 *
 * Custom nav items are stored as JSON under the key `__nav:custom-items`.
 * They are merged with the default navigation from siteConfig / storeSiteConfig.
 *
 * Supports:
 *  - Top-level items
 *  - Children (dropdown) under existing or custom parents
 *  - Item types: page, category, product, external
 *  - Hiding default nav items
 *  - Deleting items (default items AND default children go to `__nav:deleted-items`,
 *    custom items are removed from `__nav:custom-items`)
 *  - Full reordering of ALL items (defaults + custom) via `__nav:order`
 *  - Drag-and-drop reordering via `moveNavToIndex`
 *  - Label syncing: edits made inline in the template header/footer are
 *    stored under `nav:${path}` content keys. mergedNav reads these keys
 *    so every consumer gets the up-to-date label.
 *  - updateItemLabel: updates both the content key AND the CustomNavItem record.
 *  - addItem supports `hideFromMenu` flag — creates the page but immediately
 *    adds it to `__nav:hidden-items`.
 */

import { useCallback, useMemo } from "react";
import { useContentEditingSafe } from "./ContentEditingContext";
import { useTemplateType } from "./TemplateContext";
import { navigation as brandingNav, type NavItem } from "../../siteConfig";
import { navigation as storeNav, categories as storeCategories, products as storeProducts } from "../../storeSiteConfig";

/* ──────────── Types ──────────── */

export type CustomNavItemType = "page" | "category" | "product" | "external" | "dropdown";

export interface CustomNavItem {
  id: string;
  label: string;
  path: string;
  type: CustomNavItemType;
  /** If set, this item is a child of the item with this path */
  parentPath?: string;
}

export interface MergedNavItem {
  label: string;
  path: string;
  children?: { label: string; path: string }[];
  /** true if this is a default (non-custom) item */
  isDefault?: boolean;
  /** true if hidden by user */
  isHidden?: boolean;
}

/* ──────────── Constants ──────────── */

const NAV_ITEMS_KEY = "__nav:custom-items";
const NAV_HIDDEN_KEY = "__nav:hidden-items";
const NAV_ORDER_KEY = "__nav:order";
const NAV_DELETED_KEY = "__nav:deleted-items";

/* ──────────── Suggested pages for each template type ──────────── */

export interface SuggestedPage {
  label: string;
  path: string;
  type: CustomNavItemType;
}

const BRANDING_SUGGESTIONS: SuggestedPage[] = [
  { label: "\u05D3\u05E3 \u05D4\u05D1\u05D9\u05EA", path: "/", type: "page" },
  { label: "\u05D0\u05D5\u05D3\u05D5\u05EA", path: "/about", type: "page" },
  { label: "\u05E9\u05D9\u05E8\u05D5\u05EA\u05D9\u05DD", path: "/services", type: "page" },
  { label: "\u05E9\u05D9\u05E8\u05D5\u05EA \u05D0\u05F3", path: "/service-a", type: "page" },
  { label: "\u05E9\u05D9\u05E8\u05D5\u05EA \u05D1\u05F3", path: "/service-b", type: "page" },
  { label: "\u05DE\u05D5\u05E6\u05E8\u05D9\u05DD", path: "/products", type: "page" },
  { label: "\u05DE\u05D5\u05E6\u05E8 \u05D0\u05F3", path: "/product-a", type: "page" },
  { label: "\u05DE\u05D5\u05E6\u05E8 \u05D1\u05F3", path: "/product-b", type: "page" },
  { label: "\u05EA\u05D9\u05E7 \u05E2\u05D1\u05D5\u05D3\u05D5\u05EA", path: "/projects", type: "page" },
  { label: "\u05D9\u05D9\u05E2\u05D5\u05E5", path: "/consulting", type: "page" },
  { label: "\u05D1\u05DC\u05D5\u05D2", path: "/blog", type: "page" },
  { label: "\u05E6\u05D5\u05E8 \u05E7\u05E9\u05E8", path: "/contact", type: "page" },
];

function getStoreSuggestions(): SuggestedPage[] {
  const items: SuggestedPage[] = [
    { label: "\u05E8\u05D0\u05E9\u05D9", path: "/", type: "page" },
    { label: "\u05D0\u05D5\u05D3\u05D5\u05EA", path: "/about", type: "page" },
    { label: "\u05E7\u05D8\u05DC\u05D5\u05D2 \u05DE\u05D5\u05E6\u05E8\u05D9\u05DD", path: "/catalog", type: "page" },
    { label: "\u05D1\u05DC\u05D5\u05D2", path: "/blog", type: "page" },
    { label: "\u05E6\u05E8\u05D5 \u05E7\u05E9\u05E8", path: "/contact", type: "page" },
  ];
  for (const cat of storeCategories) {
    items.push({ label: cat.name, path: `/catalog/${cat.slug}`, type: "category" });
  }
  for (const prod of storeProducts) {
    items.push({ label: prod.name, path: `/catalog/${prod.categorySlug}/${prod.slug}`, type: "product" });
  }
  return items;
}

/* ──────────── Hook ──────────── */

export function useCustomNav() {
  const ctx = useContentEditingSafe();
  const templateType = useTemplateType();
  const isStore = templateType === "store";

  const defaultNav: NavItem[] = isStore ? storeNav : brandingNav;

  /* ── Build a flat set of ALL default paths (top-level + children) ── */
  const allDefaultPaths = useMemo(() => {
    const s = new Set<string>();
    for (const item of defaultNav) {
      s.add(item.path);
      if (item.children) {
        for (const ch of item.children) s.add(ch.path);
      }
    }
    return s;
  }, [defaultNav]);

  /* ── getContent reference — changes when *any* content key changes,
       which lets the mergedNav useMemo react to label edits. ── */
  const getContent = ctx?.getContent;

  // Read custom items
  const customItemsRaw = ctx?.getContent(NAV_ITEMS_KEY, "[]") ?? "[]";
  const customItems: CustomNavItem[] = useMemo(() => {
    try {
      const parsed = JSON.parse(customItemsRaw);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }, [customItemsRaw]);

  // Read hidden items (array of paths)
  const hiddenRaw = ctx?.getContent(NAV_HIDDEN_KEY, "[]") ?? "[]";
  const hiddenPaths: string[] = useMemo(() => {
    try {
      const parsed = JSON.parse(hiddenRaw);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }, [hiddenRaw]);

  // Read deleted items (array of paths — for default items/children that were "deleted")
  const deletedRaw = ctx?.getContent(NAV_DELETED_KEY, "[]") ?? "[]";
  const deletedPaths: string[] = useMemo(() => {
    try {
      const parsed = JSON.parse(deletedRaw);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }, [deletedRaw]);

  // Read custom order (array of paths, applied to top-level items)
  const orderRaw = ctx?.getContent(NAV_ORDER_KEY, "[]") ?? "[]";
  const savedOrder: string[] = useMemo(() => {
    try {
      const parsed = JSON.parse(orderRaw);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }, [orderRaw]);

  /**
   * Helper: read the user-edited label for a nav path.
   * Falls back to the supplied default if no override was ever saved.
   */
  const readLabel = useCallback(
    (path: string, fallback: string): string => {
      if (!getContent) return fallback;
      // Treat empty-string overrides as "no override" so a label never vanishes
      const val = getContent(`nav:${path}`, "");
      return val.trim() || fallback;
    },
    [getContent],
  );

  // Merge: default + custom, with hidden items flagged + label overrides
  // Deleted default items AND default children are completely excluded.
  const mergedNav: MergedNavItem[] = useMemo(() => {
    const result: MergedNavItem[] = [];
    const deletedSet = new Set(deletedPaths);

    // 1. Add default items (skip deleted)
    for (const item of defaultNav) {
      if (deletedSet.has(item.path)) continue;
      const merged: MergedNavItem = {
        label: readLabel(item.path, item.label),
        path: item.path,
        // Filter out deleted default children
        children: item.children
          ? item.children
              .filter((ch) => !deletedSet.has(ch.path))
              .map((ch) => ({
                label: readLabel(ch.path, ch.label),
                path: ch.path,
              }))
          : undefined,
        isDefault: true,
        isHidden: hiddenPaths.includes(item.path),
      };
      // Add custom children to this parent
      const customChildren = customItems.filter((c) => c.parentPath === item.path);
      if (customChildren.length > 0) {
        if (!merged.children) merged.children = [];
        for (const cc of customChildren) {
          merged.children.push({ label: readLabel(cc.path, cc.label), path: cc.path });
        }
      }
      // Clean up empty children array
      if (merged.children && merged.children.length === 0) {
        merged.children = undefined;
      }
      result.push(merged);
    }

    // 2. Add custom top-level items (no parentPath, or parentPath not matching defaults)
    const defaultPaths = new Set(defaultNav.map((n) => n.path));
    const topLevelCustom = customItems.filter(
      (c) => !c.parentPath || !defaultPaths.has(c.parentPath)
    );
    for (const item of topLevelCustom) {
      // Skip if it's a child of another custom item (dropdown or page parent)
      if (item.parentPath) {
        const parentIsCustom = customItems.some(
          (ci) => ci.path === item.parentPath && (ci.type === "dropdown" || ci.type === "page" || ci.type === "category")
        );
        if (parentIsCustom) continue;
      }
      // Also skip if the parent was a deleted default item
      if (item.parentPath && deletedSet.has(item.parentPath)) continue;
      // Attach children for dropdown AND page/category items (SiteTree category pages)
      const itemChildren = customItems
        .filter((c) => c.parentPath === item.path)
        .map((c) => ({ label: readLabel(c.path, c.label), path: c.path }));
      result.push({
        label: readLabel(item.path, item.label),
        path: item.path,
        children: itemChildren.length > 0 ? itemChildren : undefined,
        isDefault: false,
        isHidden: hiddenPaths.includes(item.path),
      });
    }

    // 3. Apply saved order (if any)
    if (savedOrder.length > 0) {
      const orderMap = new Map<string, number>();
      savedOrder.forEach((path, i) => orderMap.set(path, i));

      result.sort((a, b) => {
        const ai = orderMap.has(a.path) ? orderMap.get(a.path)! : 9999;
        const bi = orderMap.has(b.path) ? orderMap.get(b.path)! : 9999;
        if (ai !== bi) return ai - bi;
        // Fallback: preserve original insertion order
        return 0;
      });
    }

    return result;
  }, [defaultNav, customItems, hiddenPaths, deletedPaths, savedOrder, readLabel]);

  // Visible nav (for rendering in header/footer)
  const visibleNav: MergedNavItem[] = useMemo(
    () => mergedNav.filter((item) => !item.isHidden),
    [mergedNav]
  );

  // Save custom items
  const saveCustomItems = useCallback(
    (items: CustomNavItem[]) => {
      if (ctx) {
        ctx.saveContent(NAV_ITEMS_KEY, JSON.stringify(items));
      }
    },
    [ctx]
  );

  // Save order
  const saveOrder = useCallback(
    (order: string[]) => {
      if (ctx) {
        ctx.saveContent(NAV_ORDER_KEY, JSON.stringify(order));
      }
    },
    [ctx]
  );

  // Toggle hidden
  const toggleHidden = useCallback(
    (path: string) => {
      if (!ctx) return;
      const next = hiddenPaths.includes(path)
        ? hiddenPaths.filter((p) => p !== path)
        : [...hiddenPaths, path];
      ctx.saveContent(NAV_HIDDEN_KEY, JSON.stringify(next));
    },
    [ctx, hiddenPaths]
  );

  /**
   * Delete a nav item completely.
   * - Custom items: removed from `__nav:custom-items` (+ their children).
   * - Default items OR default children: added to `__nav:deleted-items`
   *   so they're excluded from mergedNav.
   * Also cleans up hidden list and order list.
   */
  const deleteItem = useCallback(
    (path: string) => {
      if (!ctx) return;
      // Check if this is a default top-level item OR a default child
      const isDefaultAny = allDefaultPaths.has(path);

      if (isDefaultAny) {
        // Add to deleted list
        if (!deletedPaths.includes(path)) {
          ctx.saveContent(NAV_DELETED_KEY, JSON.stringify([...deletedPaths, path]));
        }
      }

      // Remove any custom items with this path (custom top-level or children under it)
      const remaining = customItems.filter(
        (c) => c.path !== path && c.parentPath !== path
      );
      if (remaining.length !== customItems.length) {
        saveCustomItems(remaining);
      }

      // Clean up hidden list
      if (hiddenPaths.includes(path)) {
        ctx.saveContent(
          NAV_HIDDEN_KEY,
          JSON.stringify(hiddenPaths.filter((p) => p !== path))
        );
      }

      // Clean up order list
      if (savedOrder.includes(path)) {
        ctx.saveContent(
          NAV_ORDER_KEY,
          JSON.stringify(savedOrder.filter((p) => p !== path))
        );
      }
    },
    [ctx, allDefaultPaths, customItems, deletedPaths, hiddenPaths, savedOrder, saveCustomItems],
  );

  /**
   * Restore a previously deleted default item (top-level or child).
   */
  const restoreDeletedItem = useCallback(
    (path: string) => {
      if (!ctx) return;
      ctx.saveContent(
        NAV_DELETED_KEY,
        JSON.stringify(deletedPaths.filter((p) => p !== path))
      );
    },
    [ctx, deletedPaths],
  );

  /**
   * Build a list of deleted default items AND children (for restore UI).
   */
  const deletedDefaults = useMemo(() => {
    const deletedSet = new Set(deletedPaths);
    const items: { label: string; path: string }[] = [];
    for (const n of defaultNav) {
      // Top-level deleted
      if (deletedSet.has(n.path)) {
        items.push({ label: n.label, path: n.path });
      }
      // Children deleted
      if (n.children) {
        for (const ch of n.children) {
          if (deletedSet.has(ch.path)) {
            items.push({ label: ch.label, path: ch.path });
          }
        }
      }
    }
    return items;
  }, [defaultNav, deletedPaths]);

  /**
   * Update the display label of a nav item (default or custom).
   * Writes to both:
   *  - content key `nav:${path}` (used by template header/footer rendering)
   *  - CustomNavItem.label in `__nav:custom-items` (for custom items only)
   */
  const updateItemLabel = useCallback(
    (path: string, newLabel: string) => {
      if (!ctx) return;
      // Guard: never save an empty label — it would make the nav item invisible
      const trimmed = newLabel.trim();
      if (!trimmed) return;
      // 1. Always update the content key (the canonical display name)
      ctx.saveContent(`nav:${path}`, trimmed);

      // 2. For custom items, also update the stored CustomNavItem record
      const idx = customItems.findIndex((c) => c.path === path && !c.parentPath);
      if (idx >= 0) {
        const updated = [...customItems];
        updated[idx] = { ...updated[idx], label: trimmed };
        saveCustomItems(updated);
      }
    },
    [ctx, customItems, saveCustomItems],
  );

  /**
   * Update the display label of a child nav item.
   */
  const updateChildLabel = useCallback(
    (childPath: string, parentPath: string, newLabel: string) => {
      if (!ctx) return;
      // Guard: never save an empty label
      const trimmed = newLabel.trim();
      if (!trimmed) return;
      ctx.saveContent(`nav:${childPath}`, trimmed);

      // For custom children, update the stored record
      const idx = customItems.findIndex((c) => c.path === childPath && c.parentPath === parentPath);
      if (idx >= 0) {
        const updated = [...customItems];
        updated[idx] = { ...updated[idx], label: trimmed };
        saveCustomItems(updated);
      }
    },
    [ctx, customItems, saveCustomItems],
  );

  // Add item (with optional hideFromMenu flag)
  const addItem = useCallback(
    (item: Omit<CustomNavItem, "id"> & { hideFromMenu?: boolean }) => {
      const { hideFromMenu, ...rest } = item;
      const newItem: CustomNavItem = {
        ...rest,
        id: `nav-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
      };
      saveCustomItems([...customItems, newItem]);

      // If the caller requested hiding from menu, add to hidden paths
      if (hideFromMenu && ctx) {
        const nextHidden = [...hiddenPaths, rest.path];
        ctx.saveContent(NAV_HIDDEN_KEY, JSON.stringify(nextHidden));
      }

      // If this path was previously deleted (default item), restore it
      if (deletedPaths.includes(rest.path) && ctx) {
        ctx.saveContent(
          NAV_DELETED_KEY,
          JSON.stringify(deletedPaths.filter((p) => p !== rest.path))
        );
      }
    },
    [customItems, saveCustomItems, ctx, hiddenPaths, deletedPaths]
  );

  // Remove custom item (by id — legacy, still used for child items)
  const removeItem = useCallback(
    (id: string) => {
      saveCustomItems(customItems.filter((c) => c.id !== id));
    },
    [customItems, saveCustomItems]
  );

  // Move custom item (legacy — only custom items)
  const moveItem = useCallback(
    (id: string, direction: "up" | "down") => {
      const idx = customItems.findIndex((c) => c.id === id);
      if (idx < 0) return;
      const swapIdx = direction === "up" ? idx - 1 : idx + 1;
      if (swapIdx < 0 || swapIdx >= customItems.length) return;
      const next = [...customItems];
      [next[idx], next[swapIdx]] = [next[swapIdx], next[idx]];
      saveCustomItems(next);
    },
    [customItems, saveCustomItems]
  );

  /**
   * Reorder ANY nav item (default or custom) by its path, one step up or down.
   * This updates the global order array `__nav:order`.
   */
  const reorderNavItem = useCallback(
    (path: string, direction: "up" | "down") => {
      const currentOrder = mergedNav.map((n) => n.path);
      const idx = currentOrder.indexOf(path);
      if (idx < 0) return;
      const swapIdx = direction === "up" ? idx - 1 : idx + 1;
      if (swapIdx < 0 || swapIdx >= currentOrder.length) return;
      const next = [...currentOrder];
      [next[idx], next[swapIdx]] = [next[swapIdx], next[idx]];
      saveOrder(next);
    },
    [mergedNav, saveOrder]
  );

  /**
   * Move a nav item to a specific index (for drag-and-drop).
   */
  const moveNavToIndex = useCallback(
    (fromPath: string, toIndex: number) => {
      const currentOrder = mergedNav.map((n) => n.path);
      const fromIndex = currentOrder.indexOf(fromPath);
      if (fromIndex < 0 || fromIndex === toIndex) return;
      const next = [...currentOrder];
      const [moved] = next.splice(fromIndex, 1);
      next.splice(toIndex, 0, moved);
      saveOrder(next);
    },
    [mergedNav, saveOrder]
  );

  // Get suggestions (pages not yet in nav — include deleted defaults as available)
  const suggestions = useMemo(() => {
    const allSuggs = isStore ? getStoreSuggestions() : BRANDING_SUGGESTIONS;
    const existingPaths = new Set([
      ...mergedNav.map((n) => n.path),
      ...mergedNav.flatMap((n) => n.children?.map((c) => c.path) ?? []),
    ]);
    return allSuggs.filter((s) => !existingPaths.has(s.path));
  }, [isStore, mergedNav]);

  // Available parents for dropdown children
  const availableParents = useMemo(() => {
    return mergedNav
      .filter((n) => !n.isHidden)
      .map((n) => ({ label: n.label, path: n.path }));
  }, [mergedNav]);

  return {
    mergedNav,
    visibleNav,
    customItems,
    hiddenPaths,
    deletedPaths,
    deletedDefaults,
    addItem,
    removeItem,
    deleteItem,
    restoreDeletedItem,
    moveItem,
    reorderNavItem,
    moveNavToIndex,
    toggleHidden,
    updateItemLabel,
    updateChildLabel,
    suggestions,
    availableParents,
    isStore,
  };
}
